import { StripeElementLocale } from '@stripe/stripe-js';

/**
 * Stripe-supported locales that differ from FiLanguage format.
 * Most FiLanguage values work directly when lowercased (e.g., 'en-CA', 'fr-CA', 'en-GB', 'es-ES', 'fr-FR').
 *
 * ADD NEW ENTRIES HERE if a new FiLanguage doesn't map directly to Stripe.
 * The unit test will fail if you add a new FiLanguage without updating this map.
 *
 * Keys are FiLanguage string values (not imported to avoid circular dependencies).
 */
const STRIPE_LOCALE_OVERRIDES: Record<string, StripeElementLocale> = {
  'en-US': 'en', // Stripe uses 'en' not 'en-US'
  'de-DE': 'de', // Stripe uses 'de' not 'de-DE'
  'en-FI': 'en', // English-only locale for Finland
  'en-MT': 'en', // English-only locale for Malta
  'ro-RO': 'ro', // Stripe uses 'ro' not 'ro-RO'
  'de-AT': 'de', // Austria uses German Stripe locale
};

/**
 * Converts Fi's language setting to a Stripe-compatible locale.
 * Falls back to 'auto' (browser detection) if no language is set.
 *
 * Stripe gracefully handles unknown locales by falling back to 'auto' internally,
 * so we don't need runtime validation. Instead, we rely on an exhaustive unit test
 * that covers all FiLanguage values to catch any new languages that need overrides.
 *
 * @see https://docs.stripe.com/js/appendix/supported_locales
 */
export function getStripeLocale(language: string | undefined): StripeElementLocale {
  if (!language) {
    return 'auto';
  }

  // Check for overrides first (en-US → en, de-DE → de)
  const override = STRIPE_LOCALE_OVERRIDES[language];
  if (override) {
    return override;
  }

  // Most FiLanguage values are directly compatible with Stripe when lowercased
  // e.g., 'en-CA' → 'en-ca', 'fr-FR' → 'fr-fr'
  // If Stripe doesn't recognize the locale, it falls back to 'auto' internally
  return language.toLowerCase() as StripeElementLocale;
}
